import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setPage, usePublicState } from '@/store/slices/publicSlice';
import {
  LogisticsFeedbackItem,
  ShipmentFeedbackItem,
  ShipmentFeedbackDetailPayload,
  ShipmentFeedbackDetailResponse,
  ShipmentFeedbackMilestonesResponse,
  ShipmentFeedbackMarketplaceResponse,
  ShipmentFeedbackFullBudgetResponse,
} from '@/types/logisticsFeedback';
import {
  adaptBaseFeedbackItem,
  adaptMilestonesFeedback,
  adaptMarketplaceFeedback,
  adaptFullBudgetFeedback,
} from './logisticsFeedbackAdapters';
import { FeedbackFilterValues } from '@/components/LogisticsFeedback/FeedbackFilterModal';
import {
  useGetShipmentFeedbacksQuery,
  useGetShipmentFeedbackMilestonesQuery,
  useGetShipmentFeedbackMarketplaceQuery,
  useGetShipmentFeedbackFullBudgetQuery,
} from '@/store/services/feedbackService';

export const LOGISTICS_FEEDBACK_SEARCH_KEY = 'logisticsFeedbackSearch';
export const LOGISTICS_FEEDBACK_PAGE_SIZE = 10;

const DEFAULT_FILTERS: FeedbackFilterValues = {
  orderType: '',
  dateFrom: '',
  dateTo: '',
};

export const mapShipmentItemToLogisticsFeedback = adaptBaseFeedbackItem;

export function useLogisticsFeedback() {
  const dispatch = useDispatch();
  const { search, page } = usePublicState();

  const [filters, setFilters] = useState<FeedbackFilterValues>(DEFAULT_FILTERS);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    null
  );

  const searchValue = (search?.[LOGISTICS_FEEDBACK_SEARCH_KEY] || '').trim();
  const currentPage = page || 0;

  const {
    data: feedbackResponse,
    isLoading,
    isFetching,
  } = useGetShipmentFeedbacksQuery({
    page: currentPage,
    size: LOGISTICS_FEEDBACK_PAGE_SIZE,
    ...(searchValue ? { search: searchValue } : {}),
    ...(filters.orderType ? { requestType: filters.orderType } : {}),
    ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
    ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
  });

  const activeFiltersCount = useMemo(
    () =>
      [
        Boolean(filters.orderType),
        Boolean(filters.dateFrom),
        Boolean(filters.dateTo),
      ].filter(Boolean).length,
    [filters]
  );

  const rawList: ShipmentFeedbackItem[] =
    feedbackResponse?.payload?.content ?? feedbackResponse?.content ?? [];
  const totalElements: number =
    feedbackResponse?.payload?.totalElements ??
    feedbackResponse?.totalElements ??
    0;

  const selectedIndex = useMemo(
    () =>
      selectedFeedbackId
        ? rawList.findIndex((item) => item.id === selectedFeedbackId)
        : -1,
    [selectedFeedbackId, rawList]
  );

  const selectedListItem = selectedIndex === -1 ? null : rawList[selectedIndex];

  const isMarketplace =
    selectedListItem?.requestType?.code === 'MARKETPLACE' ||
    selectedListItem?.requestType?.code === 'STORE';
  const isMilestones =
    selectedListItem?.requestType?.code === 'BIDDING' &&
    selectedListItem?.budgetType === 'MILESTONES';
  const isFullBudget =
    selectedListItem?.requestType?.code === 'BIDDING' &&
    selectedListItem?.budgetType === 'FULL_BUDGET';

  const {
    data: milestonesDetail,
    isLoading: isMilestonesLoading,
    isFetching: isMilestonesFetching,
  } = useGetShipmentFeedbackMilestonesQuery(selectedFeedbackId ?? '', {
    skip: !selectedFeedbackId || !isMilestones,
  });

  const {
    data: marketplaceDetail,
    isLoading: isMarketplaceLoading,
    isFetching: isMarketplaceFetching,
  } = useGetShipmentFeedbackMarketplaceQuery(selectedFeedbackId ?? '', {
    skip: !selectedFeedbackId || !isMarketplace,
  });

  const {
    data: fullBudgetDetail,
    isLoading: isFullBudgetLoading,
    isFetching: isFullBudgetFetching,
  } = useGetShipmentFeedbackFullBudgetQuery(selectedFeedbackId ?? '', {
    skip: !selectedFeedbackId || !isFullBudget,
  });

  const isDetailLoading =
    (isMilestones && (isMilestonesLoading || isMilestonesFetching)) ||
    (isMarketplace && (isMarketplaceLoading || isMarketplaceFetching)) ||
    (isFullBudget && (isFullBudgetLoading || isFullBudgetFetching));

  const activeDetailResponse: ShipmentFeedbackDetailResponse | undefined =
    (isMilestones ? milestonesDetail : undefined) ??
    (isMarketplace ? marketplaceDetail : undefined) ??
    (isFullBudget ? fullBudgetDetail : undefined);

  const activeDetailPayload: ShipmentFeedbackDetailPayload | undefined =
    activeDetailResponse?.payload ??
    activeDetailResponse?.data ??
    activeDetailResponse;

  const selectedFeedbackItem = useMemo<LogisticsFeedbackItem | null>(() => {
    if (!selectedListItem) return null;
    if (!activeDetailPayload) return adaptBaseFeedbackItem(selectedListItem);

    if (isMilestones) {
      return adaptMilestonesFeedback(
        activeDetailPayload as ShipmentFeedbackMilestonesResponse,
        selectedListItem
      );
    }
    if (isFullBudget) {
      return adaptFullBudgetFeedback(
        activeDetailPayload as ShipmentFeedbackFullBudgetResponse,
        selectedListItem
      );
    }
    return adaptMarketplaceFeedback(
      activeDetailPayload as ShipmentFeedbackMarketplaceResponse,
      selectedListItem
    );
  }, [selectedListItem, activeDetailPayload, isMilestones, isFullBudget]);

  const handleNext = useCallback(() => {
    if (selectedIndex < rawList.length - 1) {
      setSelectedFeedbackId(rawList[selectedIndex + 1].id);
    }
  }, [selectedIndex, rawList]);

  const handlePrev = useCallback(() => {
    if (selectedIndex > 0) {
      setSelectedFeedbackId(rawList[selectedIndex - 1].id);
    }
  }, [selectedIndex, rawList]);

  const handleApplyFilters = useCallback(
    (newFilters: FeedbackFilterValues) => {
      setFilters(newFilters);
      dispatch(setPage(0));
    },
    [dispatch]
  );

  const handlePageChange = useCallback(
    (pageNumber: number) => dispatch(setPage(pageNumber - 1)),
    [dispatch]
  );

  return {
    filters,
    activeFiltersCount,
    rawList,
    paginatedList: rawList,
    totalElements,
    currentPage,
    isLoading,
    isFetching,
    isDetailLoading,
    selectedFeedbackItem,
    hasNext: selectedIndex !== -1 && selectedIndex < rawList.length - 1,
    hasPrev: selectedIndex > 0,
    selectFeedback: setSelectedFeedbackId,
    closeDrawer: () => setSelectedFeedbackId(null),
    handleNext,
    handlePrev,
    handleApplyFilters,
    handlePageChange,
  };
}
